<template>
  <div class="user-management">
    <!-- Toast Notifications -->
    <ToastContainer :toasts="toasts" @remove="removeToast" />
    <!-- Header -->
    <div class="page-header">
      <h1>User Management</h1>
      <p>Manage and view customer information</p>
      <div v-if="!isSuperAdmin" class="authorization-alert">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <span>You are not authorized to perform actions on this page. Only super admins can manage users.</span>
      </div>
    </div>

    <!-- Filters and Search -->
    <div class="filters-section">
      <div class="search-controls">
        <div class="search-box">
          <i class="fa-solid fa-search"></i>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search users by name, email, or customer number..."
            @input="debouncedSearch"
          >
        </div>
        
        <div class="filter-controls">
          <div class="filter-group">
            <label class="status-label">Status:</label>
            <select v-model="filters.status" @change="loadUsers">
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <button class="btn btn-secondary" @click="resetFilters">
            <i class="fa-solid fa-refresh"></i>
            Reset Filters
          </button>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="stats-cards">
      <div class="stat-card total">
        <div class="stat-icon">
          <i class="fa-solid fa-users"></i>
        </div>
        <div class="stat-content">
          <h3>{{ stats.total_users }}</h3>
          <p>Total Users</p>
        </div>
      </div>
      <div class="stat-card active">
        <div class="stat-icon">
          <i class="fa-solid fa-user-check"></i>
        </div>
        <div class="stat-content">
          <h3>{{ stats.active_users }}</h3>
          <p>Active Users</p>
        </div>
      </div>
      <div class="stat-card suspended">
        <div class="stat-icon">
          <i class="fa-solid fa-user-slash"></i>
        </div>
        <div class="stat-content">
          <h3>{{ stats.suspended_users }}</h3>
          <p>Suspended Users</p>
        </div>
      </div>
    </div>

    <!-- Users Table -->
    <div class="users-table-container">
      <div class="table-header">
        <h3>Customers</h3>
        <div class="table-actions">
          <button class="btn btn-primary" @click="exportUsers">
            <i class="fa-solid fa-download"></i>
            Export CSV
          </button>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="users-table">
          <thead>
            <tr>
              <th>Customer #</th>
              <th>User Info</th>
              <th>Contact</th>
              <th>Activity</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in paginatedUsers" :key="user.customer_number">
              <td class="customer-number">
                #{{ user.customer_number }}
              </td>
              <td class="user-info">
                <div class="user-avatar">
                  {{ getUserInitials(user.full_name) }}
                </div>
                <div class="user-details">
                  <strong>{{ user.full_name }}</strong>
                  <span class="user-email">{{ user.email }}</span>
                  <span class="user-joined">Joined {{ formatDate(user.created_at) }}</span>
                </div>
              </td>
              <td class="contact-info">
                <div class="contact-item">
                  <i class="fa-solid fa-phone"></i>
                  <span>{{ user.phone_number || 'Not provided' }}</span>
                </div>
                <div class="contact-item" v-if="user.preferred_sport">
                  <i class="fa-solid fa-baseball"></i>
                  <span>Prefers {{ user.preferred_sport }}</span>
                </div>
              </td>
              <td class="activity-info">
                <div class="activity-stats">
                  <div class="activity-item">
                    <span class="activity-label">Purchases:</span>
                    <span class="activity-value">{{ user.total_purchases }}</span>
                  </div>
                  <div class="activity-item">
                    <span class="activity-label">Last Login:</span>
                    <span class="activity-value">{{ formatRelativeTime(user.last_login_at) }}</span>
                  </div>
                </div>
              </td>
              <td class="status-info">
                <div class="status-badges">
                  <span class="status-badge" :class="user.account_status">
                    {{ user.account_status }}
                  </span>
                  <span v-if="user.is_verified" class="verification-badge verified">
                    <i class="fa-solid fa-check"></i>
                    Verified
                  </span>
                </div>
              </td>
              <td class="actions">
                <button 
                  class="btn btn-sm btn-info" 
                  @click="viewUserDetails(user)"
                  title="View Details"
                >
                  <i class="fa-solid fa-eye"></i>
                </button>
                <button 
                  v-if="user.account_status === 'active' && isSuperAdmin"
                  class="btn btn-sm btn-danger" 
                  @click="openSuspendConfirm(user)"
                  title="Suspend User"
                >
                  <i class="fa-solid fa-user-slash"></i>
                </button>
                <span 
                  v-else-if="user.account_status !== 'active'"
                  class="status-text suspended"
                >
                  Suspended
                </span>
                <span 
                  v-else-if="!isSuperAdmin"
                  class="status-text unauthorized"
                  title="Only super admins can suspend users"
                >
                  Not Authorized
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty State -->
        <div v-if="users.length === 0 && !loading" class="empty-state">
          <i class="fa-solid fa-users-slash"></i>
          <h4>No users found</h4>
          <p>There are no users matching your current filters.</p>
        </div>
      </div>

      <!-- Pagination -->
      <div class="pagination" v-if="users.length > 0">
        <button 
          class="btn btn-outline" 
          @click="previousPage"
          :disabled="pagination.currentPage === 1"
        >
          <i class="fa-solid fa-chevron-left"></i>
          Previous
        </button>
        
        <span class="page-info">
          Page {{ pagination.currentPage }} of {{ totalPages }} ({{ users.length }} total users)
        </span>
        
        <button 
          class="btn btn-outline" 
          @click="nextPage"
          :disabled="pagination.currentPage === totalPages"
        >
          Next
          <i class="fa-solid fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- User Details Modal -->
    <div v-if="selectedUser" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>User Details - {{ selectedUser.full_name }}</h3>
          <button class="close-btn" @click="closeModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="user-details-grid">
            <!-- Basic Information -->
            <div class="detail-section">
              <h4>Basic Information</h4>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>Customer Number:</label>
                  <span>#{{ selectedUser.customer_number }}</span>
                </div>
                <div class="detail-item">
                  <label>Full Name:</label>
                  <span>{{ selectedUser.full_name }}</span>
                </div>
                <div class="detail-item">
                  <label>Email:</label>
                  <span>{{ selectedUser.email }}</span>
                </div>
                <div class="detail-item">
                  <label>Phone:</label>
                  <span>{{ selectedUser.phone_number || 'Not provided' }}</span>
                </div>
                <div class="detail-item">
                  <label>Preferred Sport:</label>
                  <span>{{ selectedUser.preferred_sport || 'Not specified' }}</span>
                </div>
              </div>
            </div>

            <!-- Account Status -->
            <div class="detail-section">
              <h4>Account Status</h4>
              <div class="status-grid">
                <div class="status-item">
                  <label>Account Status:</label>
                  <span class="status-badge" :class="selectedUser.account_status">
                    {{ selectedUser.account_status }}
                  </span>
                </div>
                <div class="status-item">
                  <label>Verification:</label>
                  <span class="verification-badge" :class="selectedUser.is_verified ? 'verified' : 'unverified'">
                    <i class="fa-solid" :class="selectedUser.is_verified ? 'fa-check' : 'fa-times'"></i>
                    {{ selectedUser.is_verified ? 'Verified' : 'Unverified' }}
                  </span>
                </div>
                <div class="status-item">
                  <label>Last Login:</label>
                  <span>{{ selectedUser.last_login_at ? formatRelativeTime(selectedUser.last_login_at) : 'Never' }}</span>
                </div>
              </div>
            </div>

            <!-- Activity Metrics -->
            <div class="detail-section">
              <h4>Activity Metrics</h4>
              <div class="metrics-grid">
                <div class="metric-card">
                  <div class="metric-value">{{ selectedUser.total_purchases }}</div>
                  <div class="metric-label">Total Purchases</div>
                </div>
                <div class="metric-card">
                  <div class="metric-value">{{ selectedUser.total_pitches_booked }}</div>
                  <div class="metric-label">Pitches Booked</div>
                </div>
                <div class="metric-card">
                  <div class="metric-value">{{ selectedUser.total_matches_organized }}</div>
                  <div class="metric-label">Matches Organized</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button 
            v-if="selectedUser.account_status === 'active' && isSuperAdmin"
            class="btn btn-danger" 
            @click="openSuspendConfirm(selectedUser)"
          >
            <i class="fa-solid fa-user-slash"></i>
            Suspend User
          </button>
          <button class="btn btn-outline" @click="closeModal">
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Suspend Confirmation Modal -->
    <div v-if="userToSuspend" class="modal-overlay suspend-confirm-overlay" @click="closeSuspendConfirm">
      <div class="modal-content suspend-confirm-modal" @click.stop>
        <div class="modal-header suspend-confirm-header">
          <i class="fa-solid fa-user-slash"></i>
          <h3>Suspend User</h3>
          <button class="close-btn" @click="closeSuspendConfirm">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to suspend <strong>{{ userToSuspend.full_name }}</strong>?</p>
          <p class="suspend-warning">This will prevent them from accessing their account until reinstated.</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-outline" @click="closeSuspendConfirm">
            Cancel
          </button>
          <button class="btn btn-danger" @click="confirmSuspendUser" :disabled="suspending">
            <i class="fa-solid fa-user-slash"></i>
            {{ suspending ? 'Suspending...' : 'Suspend User' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
/**
 * ListCustomer - Admin User Management Component
 *
 * Manages customer list view for super admins. Provides:
 * - Search and filter by status (all, active, suspended)
 * - User statistics cards (total, active, suspended)
 * - Paginated table with user details
 * - User details modal
 * - Suspend user action
 *
 * API Endpoints (backend: application/routes/admin/super_admin_functions/users.py):
 * - GET /admin/users?status=&search= - List users with filters
 * - GET /admin/users/stats - Dashboard statistics
 * - PUT /admin/users/{customer_number}/suspend - Suspend user
 *
 * @module components/adminpage/ListCustomer
 * @component UserManagement
 */

import ToastContainer from '@/components/ui/ToastContainer.vue';
import globalLoadingMixin from "@/mixins/globalLoadingMixin";
import toastMixin from "@/mixins/toastMixin";
import { buildApiUrl } from '@/config/api';
import { useAdminAuthStore } from '@/authen/adminAuth';

const DEBOUNCE_MS = 500;
const PAGE_SIZE = 10;

export default {
  name: 'UserManagement',
  loadingMessage: 'Loading users...',
  mixins: [toastMixin, globalLoadingMixin],

  components: { ToastContainer },

  data() {
    return {
      loading: false,
      users: [],
      selectedUser: null,
      searchQuery: '',
      filters: { status: 'all' },
      stats: { total_users: 0, active_users: 0, suspended_users: 0 },
      pagination: { currentPage: 1, pageSize: PAGE_SIZE },
      debounceTimer: null,
      userToSuspend: null,
      suspending: false,
    };
  },

  computed: {
    paginatedUsers() {
      const { currentPage, pageSize } = this.pagination;
      const start = (currentPage - 1) * pageSize;
      return this.users.slice(start, start + pageSize);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.users.length / this.pagination.pageSize));
    },
    isSuperAdmin() {
      const adminStore = useAdminAuthStore();
      return adminStore.admin?.role?.toLowerCase() === 'super_admin';
    },
  },

  mounted() {
    const adminStore = useAdminAuthStore();
    adminStore.checkToken();
    
    if (!adminStore.isAuthenticated) {
      this.showToast('error', 'Authentication Required', 'Please log in as admin to access this page.');
      return;
    }
    
    this.loadUsers();
    this.loadStats();
  },

  methods: {
    _authHeaders() {
      const adminStore = useAdminAuthStore();
      const token = adminStore.accessToken || localStorage.getItem('adminAccessToken');
      return token ? { Authorization: `Bearer ${token}` } : {};
    },

    async loadUsers() {
      this.loading = true;
      this.pagination.currentPage = 1;
      try {
        const params = new URLSearchParams();
        if (this.filters.status !== 'all') params.append('status', this.filters.status);
        if (this.searchQuery?.trim()) params.append('search', this.searchQuery.trim());

        const response = await fetch(
          `${buildApiUrl('/admin/users')}?${params.toString()}`,
          { headers: this._authHeaders() }
        );

        if (!response.ok) throw new Error('Failed to load users');
        this.users = await response.json();
      } catch (error) {
        console.error('ListCustomer loadUsers:', error);
        this.showToast('error', 'Load Failed', 'Could not load users. Please try again.');
      } finally {
        this.loading = false;
      }
    },

    async loadStats() {
      try {
        const response = await fetch(buildApiUrl('/admin/users/stats'), {
          headers: this._authHeaders(),
        });
        if (!response.ok) throw new Error('Failed to load stats');
        this.stats = await response.json();
      } catch (error) {
        console.error('ListCustomer loadStats:', error);
        this.showToast('error', 'Stats Error', 'Could not load statistics.');
      }
    },

    debouncedSearch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.loadUsers(), DEBOUNCE_MS);
    },

    getUserInitials(fullName) {
      if (!fullName) return '?';
      return fullName
        .split(' ')
        .map((n) => n.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },

    formatRelativeTime(dateString) {
      if (!dateString) return 'Never';
      const diffDays = Math.ceil(
        Math.abs(Date.now() - new Date(dateString)) / (1000 * 60 * 60 * 24)
      );
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      return `${Math.floor(diffDays / 30)} months ago`;
    },

    viewUserDetails(user) {
      this.selectedUser = user;
    },

    closeModal() {
      this.selectedUser = null;
    },

    openSuspendConfirm(user) {
      this.userToSuspend = user;
    },

    closeSuspendConfirm() {
      this.userToSuspend = null;
      this.suspending = false;
    },

    async confirmSuspendUser() {
      if (!this.isSuperAdmin) {
        this.showToast('error', 'Unauthorized', 'Only super admins can suspend users.');
        this.closeSuspendConfirm();
        return;
      }

      const user = this.userToSuspend;
      if (!user) return;

      this.suspending = true;
      try {
        const response = await fetch(
          buildApiUrl(`/admin/users/${user.customer_number}/suspend`),
          {
            method: 'PUT',
            headers: this._authHeaders(),
          }
        );

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || 'Failed to suspend user');
        }

        this.showToast('success', 'User Suspended', `${user.full_name} has been suspended.`);
        this.closeSuspendConfirm();
        this.closeModal();
        await Promise.all([this.loadUsers(), this.loadStats()]);
      } catch (error) {
        console.error('ListCustomer suspendUser:', error);
        this.showToast(
          'error',
          'Suspend Failed',
          error.message || 'Could not suspend user. Please try again.'
        );
      } finally {
        this.suspending = false;
      }
    },

    exportUsers() {
      this.showToast('info', 'Coming Soon', 'CSV export will be available in a future update.');
    },

    resetFilters() {
      this.filters.status = 'all';
      this.searchQuery = '';
      this.loadUsers();
    },

    nextPage() {
      if (this.pagination.currentPage < this.totalPages) {
        this.pagination.currentPage++;
      }
    },

    previousPage() {
      if (this.pagination.currentPage > 1) {
        this.pagination.currentPage--;
      }
    },
  },
};
</script>

<style scoped>
.user-management {
  padding: 2rem;
  background: var(--background-color1);
  min-height: 100vh;
}

.page-header {
  margin-bottom: 2rem;
}

.page-header h1 {
  color: var(--text-color1);
  margin-bottom: 0.5rem;
}

.page-header p {
  color: var(--text-color2);
}

.authorization-alert {
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(220, 53, 69, 0.1);
  border: 2px solid #dc3545;
  border-radius: 8px;
  color: #dc3545;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
}

.authorization-alert i {
  font-size: 1.2rem;
}

.status-text.unauthorized {
  color: #dc3545;
  font-size: 0.8rem;
  font-weight: 600;
  font-style: italic;
}

/* Search and Filters */
.filters-section {
  margin-bottom: 2rem;
}
.status-label {
  color: var(--text-color1);
}
.search-controls {
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 300px;
}

.search-box i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2);
}

.search-box input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: var(--background-color2);
}

.filter-controls {
  display: flex;
  gap: 1rem;
  align-items: end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  font-weight: 600;
  color: var(--text-color1);
  font-size: 0.9rem;
}

.filter-group select {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: var(--background-color2);
}

/* Stats Cards */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-radius: 8px;
  background: var(--background-color2);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.stat-icon {
  font-size: 2rem;
  margin-right: 1rem;
}

.stat-card.total .stat-icon { color: #007bff; }
.stat-card.active .stat-icon { color: #28a745; }
.stat-card.suspended .stat-icon { color: #dc3545; }

.stat-content h3 {
  font-size: 1.5rem;
  margin: 0;
  color: var(--text-color1);
}

.table-header h3{
   color: var(--text-color1);
}

.stat-content p {
  margin: 0;
  color: var(--text-color2);
  font-size: 0.9rem;
}

/* Table Styles */
.users-table-container {
  background: var(--background-color2);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
}

.table-wrapper {
  overflow-x: auto;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
}

.users-table th,
.users-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.users-table th {
  background: var(--background-color2);
  font-weight: 600;
  color: var(--text-color1);
  position: sticky;
  top: 0;
}

.users-table tbody tr:hover {
  background: #52525293;
}

/* Specific column styles */
.customer-number {
  font-weight: 600;
  color: var(--text-color1);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--border-color-hover);
  color: var(--button-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.user-details {
  display: flex;
  flex-direction: column;
}

.user-details strong {
  color: var(--text-color1);
  margin-bottom: 0.25rem;
}

.user-email {
  font-size: 0.8rem;
  color: var(--text-color2);
}

.user-joined {
  font-size: 0.7rem;
  color: var(--text-color2);
}

.contact-info {
  min-width: 150px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  font-size: 0.8rem;
}

.contact-item i {
  width: 12px;
  color: var(--text-color2);
}

.activity-info {
  min-width: 120px;
}

.activity-stats {
  margin-bottom: 0.5rem;
}

.activity-item {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  margin-bottom: 0.1rem;
}

.activity-label {
  color: var(--text-color2);
}

.activity-value {
  font-weight: 600;
  color: var(--text-color1);
}

.status-info {
  min-width: 120px;
}

.status-badges {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.status-badge {
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: capitalize;
  display: inline-block;
  text-align: center;
}

.status-badge.active {
  background: #d4edda;
  color: #155724;
}

.status-badge.suspended {
  background: #f8d7da;
  color: #721c24;
}

.verification-badge {
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.verification-badge.verified {
  background: #d1ecf1;
  color: #0c5460;
}

.verification-badge.unverified {
  background: #f8d7da;
  color: #721c24;
}

.actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.actions .btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
}

.status-text.suspended {
  color: #dc3545;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: var(--text-color2);
}

.modal-body {
  padding: 1.5rem;
}

.user-details-grid {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.detail-section h4 {
  margin-bottom: 1rem;
  color: var(--text-color1);
  border-bottom: 1px solid #eee;
  padding-bottom: 0.5rem;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.detail-item label {
  font-weight: 600;
  color: var(--text-color1);
  font-size: 0.9rem;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
}

.metric-card {
  text-align: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.metric-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--border-color-hover);
  margin-bottom: 0.5rem;
}

.metric-label {
  font-size: 0.8rem;
  color: var(--text-color2);
}

.modal-footer {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 1.5rem;
  border-top: 1px solid #eee;
}

/* Suspend confirmation modal - higher z-index to overlay user details */
.suspend-confirm-overlay {
  z-index: 1100;
}

.suspend-confirm-modal {
  max-width: 420px;
}

.suspend-confirm-header > i:first-child {
  font-size: 1.5rem;
  color: #dc3545;
  margin-right: 0.5rem;
}

.suspend-warning {
  color: #721c24;
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem;
  color: var(--text-color2);
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #ddd;
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255,255,255,0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--border-color-hover);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-top: 1px solid #eee;
}

.page-info {
  color: var(--text-color2);
  font-weight: 600;
}

/* Responsive Design */
@media (max-width: 768px) {
  .user-management {
    padding: 1rem;
  }

  .search-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    min-width: auto;
  }

  .filter-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .table-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .modal-content {
    width: 95%;
    margin: 1rem;
  }

  .modal-footer {
    flex-direction: column;
  }

  .stats-cards {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 480px) {
  .stats-cards {
    grid-template-columns: 1fr;
  }
}
</style>